import {isInteractiveTarget} from './keyboard-navigation';
export type WheelStep = 'forward' | 'backward';

const PIXELS_PER_LINE = 16;
const PIXELS_PER_PAGE = 900;
const DELTA_LINE = 1;
const DELTA_PAGE = 2;

export class WheelStepController {
  #distance = 0;
  #direction = 0;
  #lastEventAt = Number.NEGATIVE_INFINITY;
  #locked = false;

  constructor(
    private readonly threshold = 30,
    private readonly gestureGap = 180,
  ) {}

  consume(deltaY: number, deltaMode: number, now: number): WheelStep | null {
    if (!Number.isFinite(deltaY) || deltaY === 0) return null;

    const distance =
      deltaY *
      (deltaMode === DELTA_LINE
        ? PIXELS_PER_LINE
        : deltaMode === DELTA_PAGE
          ? PIXELS_PER_PAGE
          : 1);
    const direction = Math.sign(distance);

    if (now - this.#lastEventAt > this.gestureGap) {
      this.#distance = 0;
      this.#direction = direction;
      this.#locked = false;
    }
    this.#lastEventAt = now;

    if (this.#locked) return null;
    if (direction !== this.#direction) {
      this.#distance = 0;
      this.#direction = direction;
    }
    this.#distance += Math.abs(distance);
    if (this.#distance < this.threshold) return null;

    this.#locked = true;
    return direction > 0 ? 'forward' : 'backward';
  }
}

export function bindWheelNavigation(
  target: HTMLElement,
  navigate: (step: WheelStep) => void,
): () => void {
  const controller = new WheelStepController();
  const onWheel = (event: WheelEvent) => {
    if (event.ctrlKey || isInteractiveTarget(event.target) || Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
    event.preventDefault();
    const step = controller.consume(event.deltaY, event.deltaMode, event.timeStamp);
    if (step) navigate(step);
  };
  target.addEventListener('wheel', onWheel, {passive: false});
  return () => target.removeEventListener('wheel', onWheel);
}
